import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product } from '../../types';
import { Coupon, db } from '../services/backend';
import { trackPixelEvent } from '../utils/pixel';

export interface CartItem extends Product {
  quantity: number;
  selectedKitVariations?: {
    [productId: string]: { id: string; label: string; productName?: string };
  }; // productId -> { id, label, productName }
}

export interface ShippingOption {
  id: number | string; // Support both API IDs (number) and custom IDs (string) like 'store-pickup'
  name: string;
  company: {
    id?: number;
    name: string;
    picture: string;
  };
  price: string | number; // Support both string (API) and number (custom)
  delivery_range: {
    min: number;
    max: number;
  };
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (
    product: Product,
    quantity?: number,
    selectedKitVariations?: {
      [productId: string]: { id: string; label: string; productName?: string };
    }
  ) => void;
  removeFromCart: (
    productId: string,
    selectedKitVariations?: {
      [productId: string]: { id: string; label: string; productName?: string };
    }
  ) => void;
  updateQuantity: (
    productId: string,
    quantity: number,
    selectedKitVariations?: {
      [productId: string]: { id: string; label: string; productName?: string };
    }
  ) => void;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
  isCartOpen: boolean;
  setIsCartOpen: (isOpen: boolean) => void;
  selectedShipping: ShippingOption | null;
  setSelectedShipping: (shipping: ShippingOption | null) => void;
  shippingCost: number;
  grandTotal: number;
  coupon: Coupon | null;
  applyCoupon: (code: string) => Promise<boolean>;
  removeCoupon: () => void;
  discountAmount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem('Veraz_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  const [selectedShipping, setSelectedShipping] = useState<ShippingOption | null>(() => {
    const savedShipping = localStorage.getItem('Veraz_shipping');
    return savedShipping ? JSON.parse(savedShipping) : null;
  });

  const [coupon, setCoupon] = useState<Coupon | null>(() => {
    const savedCoupon = localStorage.getItem('Veraz_coupon');
    return savedCoupon ? JSON.parse(savedCoupon) : null;
  });

  // Persistence Effects (Save on Change)
  useEffect(() => {
    localStorage.setItem('Veraz_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    if (selectedShipping) {
      localStorage.setItem('Veraz_shipping', JSON.stringify(selectedShipping));
    } else {
      localStorage.removeItem('Veraz_shipping');
    }
  }, [selectedShipping]);

  useEffect(() => {
    if (coupon) {
      localStorage.setItem('Veraz_coupon', JSON.stringify(coupon));
    } else {
      localStorage.removeItem('Veraz_coupon');
    }
  }, [coupon]);

  const areVariationsEqual = (
    v1?: { [key: string]: { id: string; label: string } },
    v2?: { [key: string]: { id: string; label: string } }
  ) => {
    if (!v1 && !v2) return true;
    if (!v1 || !v2) return false;

    const keys1 = Object.keys(v1);
    const keys2 = Object.keys(v2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (v1[key].id !== v2[key].id) return false;
    }

    return true;
  };

  const addToCart = (
    product: Product,
    quantity = 1,
    selectedKitVariations?: { [productId: string]: { id: string; label: string } }
  ) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          item.id === product.id &&
          areVariationsEqual(item.selectedKitVariations, selectedKitVariations)
      );

      if (existingIndex >= 0) {
        const newCart = [...prev];
        newCart[existingIndex] = {
          ...newCart[existingIndex],
          quantity: newCart[existingIndex].quantity + quantity,
        };
        return newCart;
      }
      return [...prev, { ...product, quantity, selectedKitVariations }];
    });
    setIsCartOpen(true);

    trackPixelEvent('AddToCart', {
      content_ids: [product.id],
      content_type: 'product',
      value: (product.salePrice ?? product.price ?? 0) * quantity,
      currency: 'BRL',
      content_name: product.name,
    });
  };

  const removeFromCart = (
    productId: string,
    selectedKitVariations?: { [productId: string]: { id: string; label: string } }
  ) => {
    setCart((prev) =>
      prev.filter(
        (item) =>
          !(
            item.id === productId &&
            areVariationsEqual(item.selectedKitVariations, selectedKitVariations)
          )
      )
    );
  };

  const updateQuantity = (
    productId: string,
    quantity: number,
    selectedKitVariations?: { [productId: string]: { id: string; label: string } }
  ) => {
    if (quantity < 1) {
      removeFromCart(productId, selectedKitVariations);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.id === productId &&
        areVariationsEqual(item.selectedKitVariations, selectedKitVariations)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setSelectedShipping(null);
    setCoupon(null);
  };

  const applyCoupon = async (code: string): Promise<boolean> => {
    try {
      const coupons = await db.getCoupons();
      const found = coupons.find(
        (c) => c.code.toUpperCase() === code.toUpperCase() && c.status === 'active'
      );

      if (found) {
        // Check expiration
        if (new Date(found.expiresAt) < new Date()) {
          return false;
        }
        // Check usage limit
        if (found.maxUses > 0 && found.usedCount >= found.maxUses) {
          return false;
        }

        setCoupon(found);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error applying coupon:', error);
      return false;
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
  };

  const cartTotal = cart.reduce((total, item) => {
    const price = item.salePrice || item.price || 0;
    return total + price * item.quantity;
  }, 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  const shippingCost = selectedShipping
    ? typeof selectedShipping.price === 'number'
      ? selectedShipping.price
      : parseFloat(selectedShipping.price)
    : 0;

  let discountAmount = 0;
  if (coupon) {
    if (coupon.type === 'percent') {
      discountAmount = (cartTotal * coupon.value) / 100;
    } else {
      discountAmount = coupon.value;
    }
  }
  // Ensure discount doesn't exceed total
  if (discountAmount > cartTotal) discountAmount = cartTotal;

  const grandTotal = Math.max(0, cartTotal + shippingCost - discountAmount);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        cartCount,
        isCartOpen,
        setIsCartOpen,
        selectedShipping,
        setSelectedShipping,
        shippingCost,
        grandTotal,
        coupon,
        applyCoupon,
        removeCoupon,
        discountAmount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
